import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Building2, TrendingUp, Globe, Award, Calendar, ArrowRight, ShieldCheck, Lightbulb, HardHat, Handshake, Star } from 'lucide-react';

const AboutUs = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen font-sans text-slate-800 overflow-x-hidden" ref={ref} style={{ fontFamily: "Monstserrat, proxima-nova, Helveti, sans-serif" }}>
      <div className="relative h-[60vh] sm:h-[75vh] min-h-[500px] lg:min-h-[600px] 2xl:min-h-[800px] flex items-center justify-center overflow-hidden">
        {/* Parallax Background */}
        <motion.div
          style={{ y: backgroundY }}
          className="absolute inset-0 z-0"
        >
          <img
            src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=2689&auto=format&fit=crop"
            alt="Skyscraper Construction"
            className="w-full h-full object-cover scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/50 to-slate-50"></div>
        </motion.div>

        {/* Hero Content */}
        <motion.div
          style={{ y: textY }}
          className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center text-white"
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <div className="inline-flex items-center gap-2 py-1.5 px-3 sm:py-2 sm:px-4 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-xs sm:text-sm font-semibold tracking-widest mb-4 sm:mb-6 uppercase">
              <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-[#2a3890]"></span>
              Since 2008
            </div>
            {/* Granular Typography for all breakpoints */}
            <h1 className="text-3xl min-[375px]:text-4xl sm:text-5xl md:text-6xl lg:text-7xl 2xl:text-8xl min-[2000px]:text-9xl font-bold mb-4 sm:mb-6 leading-tight tracking-tight">
              Constructing <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">Tomorrow</span> {" "} Today
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl 2xl:text-3xl text-slate-200 max-w-xs sm:max-w-2xl lg:max-w-3xl mx-auto leading-relaxed font-light">
              Transforming skylines and communities through 16+ years of engineering excellence and strategic development.
            </p>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, delay: 1 }}
          className="absolute bottom-6 sm:bottom-10 left-1/2 -translate-x-1/2 text-white/50 hidden sm:block"
        >
          <div className="w-[1px] h-12 sm:h-16 bg-gradient-to-b from-white to-transparent mx-auto"></div>
        </motion.div>
      </div>

      {/* 2. STATS SECTION (Glassmorphism) */}
      <div className="relative z-20 container mx-auto px-4 sm:px-6 lg:px-8 -mt-16 sm:-mt-24 mb-16 sm:mb-24">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="bg-transparent backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/20 p-6 sm:p-10 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10"
        >
          {[
            { icon: Calendar, label: "Years of Experience", value: "16+" },
            { icon: TrendingUp, label: "Industry Experience", value: "10+ Years" },
            { icon: Building2, label: "Completed Initiatives", value: "15+" },
            { icon: Globe, label: "Service Categories", value: "8+" },
          ].map((stat, index) => (
            <motion.div key={index} variants={fadeInUp} className="text-center relative">
              <div className="absolute inset-0 bg-blue-50/50 rounded-lg transform rotate-3 scale-90 -z-10 group-hover:rotate-0 transition-transform"></div>
              <div className="w-10 h-10 sm:w-14 sm:h-14 mx-auto bg-gradient-to-br from-[#2a3890] to-blue-700 rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-5 shadow-lg shadow-blue-900/20 transform transition-transform hover:scale-110 duration-300">
                <stat.icon className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
              </div>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl 2xl:text-5xl font-extrabold text-slate-900 mb-1 sm:mb-2 tracking-tight">{stat.value}</h3>
              <p className="text-xs sm:text-sm 2xl:text-base text-slate-500 font-bold uppercase tracking-wider">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* 3. MISSION STATEMENT (Typography) */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 mb-16 sm:mb-24 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="max-w-xs sm:max-w-2xl lg:max-w-4xl mx-auto"
        >
          <h2 className="text-2xl sm:text-3xl md:text-5xl 2xl:text-6xl font-serif text-slate-900 leading-snug">
            "We don't just build structures; we build <span className="italic text-[#2a3890]">legacies</span>. Our commitment to quality is the cornerstone of every project we touch."
          </h2>
          <div className="mt-6 sm:mt-8 w-16 sm:w-24 h-1 bg-[#2a3890] mx-auto"></div>
        </motion.div>
      </section>

      {/* 4. EXECUTIVE SUMMARY (Overlapping Layout) */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 mb-24 sm:mb-32">
        <div className="flex flex-col lg:flex-row items-center gap-10 sm:gap-16">

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="lg:w-1/2 order-2 lg:order-1"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="h-[1px] w-8 sm:w-12 bg-[#2a3890]"></span>
              <span className="text-[#2a3890] font-bold uppercase tracking-widest text-xs sm:text-sm">Who We Are</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl 2xl:text-6xl font-bold text-slate-900 mb-6 sm:mb-8 leading-tight">
              A Global Leader in <br />Construction & Development
            </h2>
            <div className="prose prose-base sm:prose-lg 2xl:prose-xl text-slate-600 space-y-4 sm:space-y-6">
              <p>
                TM International Group LLC is a premier global conglomerate with over 16 years of demonstrated excellence. Since our establishment in 2008, we have built a reputation for quality, innovation, and unwavering commitment to client satisfaction.
              </p>
              <p>
                Our diversified portfolio encompasses construction services, real estate development, investment opportunities, and consulting services. We serve residential, commercial, and government clients with distinction.
              </p>
            </div>

            <motion.button
              whileHover={{ x: 10 }}
              className="mt-8 sm:mt-10 flex items-center gap-3 text-white bg-[#2a3890] px-6 py-3 sm:px-8 sm:py-4 font-semibold text-sm sm:text-base shadow-lg shadow-blue-900/20 hover:bg-transparent hover:text-[#2a3890] hover:border hover:border-[#2a3890] transition-colors"
              onClick={() => window.location.href = '/portfolio-summary'}
            >
              Explore Our Portfolio
              <ArrowRight size={18} className="sm:w-5 sm:h-5" />
            </motion.button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:w-1/2 relative min-h-[300px] sm:min-h-[400px] lg:min-h-[500px] w-full order-1 lg:order-2"
          >
            {/* Image 1 */}
            <div className="absolute top-0 right-0 w-4/5 h-4/5 rounded-2xl overflow-hidden shadow-2xl z-10">
              <img
                src="https://images.unsplash.com/photo-1541976590-713941681591?q=80&w=2070&auto=format&fit=crop"
                alt="Construction"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            {/* Image 2 (Overlapping) */}
            <div className="absolute bottom-0 left-0 w-3/5 h-3/5 rounded-2xl overflow-hidden shadow-2xl border-4 sm:border-8 border-slate-50 z-20">
              <img
                src="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?q=80&w=2070&auto=format&fit=crop"
                alt="Planning"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            {/* Decorative Dot Grid */}
            <div className="absolute top-5 sm:top-10 left-5 sm:left-10 w-24 sm:w-32 h-24 sm:h-32 opacity-20 bg-[radial-gradient(#2a3890_2px,transparent_2px)] [background-size:16px_16px]"></div>
          </motion.div>
        </div>
      </section>

      {/* 5. CORE VALUES (New Section) */}
      <section className="bg-[#0f172a] py-16 sm:py-24 text-white relative overflow-hidden">
        {/* Abstract Background Shapes */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10">
          <div className="absolute -top-24 -left-24 w-72 sm:w-96 h-72 sm:h-96 rounded-full bg-blue-600 blur-3xl"></div>
          <div className="absolute top-1/2 right-0 w-48 sm:w-64 h-48 sm:h-64 rounded-full bg-purple-600 blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">Our Core Values</h2>
            <p className="text-sm sm:text-base text-slate-400 px-4">The principles that guide every decision we make and every structure we build.</p>
          </div>

          {/* Responsive Grid: 1 col (mobile), 2 cols (tablet), 3 cols (desktop) */}
          <div className="grid grid-cols-1 min-[600px]:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              { icon: ShieldCheck, title: "Integrity", desc: "We uphold the highest standards of honesty and transparency in all our dealings." },
              { icon: Lightbulb, title: "Innovation", desc: "We constantly seek new methods and technologies to deliver better results." },
              { icon: HardHat, title: "Excellence", desc: "Quality is not an act, it is a habit. We strive for perfection in every detail." }
            ].map((val, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -10 }}
                className="bg-white/5 backdrop-blur-sm p-6 sm:p-8 rounded-2xl border border-white/10 hover:border-[#2a3890]/50 hover:bg-white/10 transition-all duration-300"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#2a3890] rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                  <val.icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">{val.title}</h3>
                <p className="text-sm sm:text-base text-slate-400 leading-relaxed">{val.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5.5 UNIQUE SELLING PROPOSITION (USP) */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#2a3890] mb-4">
              Why Choose TM International?
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              We're not just another construction company. Here's what sets us apart from the competition.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {[
              {
                title: "16+ Years of Proven Excellence",
                description: "Since 2008, we've built a reputation for delivering exceptional results on time and within budget. Our extensive experience across residential, commercial, and government projects ensures your vision becomes reality.",
                icon: <Award />
              },
              {
                title: "Licensed & Fully Insured Professionals",
                description: "All our contractors are licensed in Virginia and Delaware, fully insured, and EPA Lead-Safe certified. Your project is in the hands of qualified experts who prioritize safety and compliance.",
                icon: <ShieldCheck />
              },
              {
                title: "Quality Guaranteed, Every Time",
                description: "We stand behind our work with comprehensive warranties and a commitment to perfection. From initial consultation to final walkthrough, quality is never compromised.",
                icon: <Star />
              },
              {
                title: "Client-Focused Approach",
                description: "Your satisfaction is our priority. We maintain transparent communication, provide detailed project timelines, and ensure you're informed every step of the way. No surprises, just results.",
                icon: <Handshake />
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-slate-50 to-blue-50 p-8 rounded-2xl border border-slate-200 hover:border-[#2a3890] hover:shadow-xl transition-all duration-300"
              >
                <div className="text-5xl mb-4">{item.icon}</div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. COMPANY HISTORY (Enhanced Timeline) */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12 sm:mb-20"
          >
            <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl text-[#2a3890] font-bold mb-4">Our Legacy</h2>
              {/* <p className="text-sm sm:text-base text-slate-400 px-4">The principles that guide every decision we make and every structure we build.</p> */}
            </div>
          </motion.div>

          <div className="max-w-4xl mx-auto relative">
            {/* Center Line Gradient - Responsive positioning */}
            <div className="absolute left-4 sm:left-6 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#2a3890] via-blue-300 to-transparent md:-translate-x-1/2"></div>

            <div className="space-y-10 sm:space-y-16">
              {[
                { year: '2025', title: 'Visionary Leap', desc: 'Pioneering next-gen AI solutions for sustainable infrastructure.', align: 'left' },
                { year: '2024', title: 'Strategic Alliances', desc: 'Forming key global partnerships to enhance service delivery.', align: 'right' },
                { year: '2023', title: 'Digital Evolution', desc: 'Launch of our digital platform AskMYteam.com.', align: 'left' },
                { year: '2020', title: 'Corporate Growth', desc: 'Delaware incorporation for specialized investment services.', align: 'right' },
                { year: '2015', title: 'Sustainable Future', desc: 'Launched Maryland Sustainable Development Division.', align: 'left' },
                { year: '2012', title: 'Capital Expansion', desc: 'Strategic expansion into the Washington DC market.', align: 'right' },
                { year: '2008', title: 'The Foundation', desc: 'TM International Group LLC founded to transform the industry.', align: 'left' },

              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ delay: 0.1 }}
                  className={`flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-8 ${item.align === 'right' ? 'md:flex-row-reverse' : ''}`}
                >
                  {/* Content */}
                  <div className={`w-full md:w-1/2 ${item.align === 'right' ? 'md:text-right' : 'md:text-left'} pl-12 sm:pl-16 md:pl-0 pr-4`}>
                    <div className="relative bg-white p-5 sm:p-6 rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 hover:border-blue-200 transition-colors group">
                      <span className={`text-4xl sm:text-5xl font-black text-slate-100 absolute -mt-8 sm:-mt-10 z-0 group-hover:text-blue-50 transition-colors duration-300 
                          ${item.align === 'right'
                          ? 'left-0 -ml-2 sm:-ml-4 md:left-auto md:right-0 md:-mr-4 md:ml-0'
                          : 'left-0 -ml-2 sm:-ml-4'
                        }`}
                      >
                        {item.year}
                      </span>
                      <h3 className="text-lg sm:text-xl font-bold text-[#2a3890] relative z-10 mb-2">{item.title}</h3>
                      <p className="text-slate-600 text-sm relative z-10 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>

                  {/* Dot */}
                  <div className="absolute left-4 sm:left-6 md:left-1/2 w-4 h-4 bg-[#2a3890] rounded-full border-4 border-white shadow-md transform -translate-x-1/2 mt-5 sm:mt-6 md:mt-0 z-10"></div>

                  {/* Empty spacer for alignment */}
                  <div className="hidden md:block w-1/2"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 7. ENHANCED CTA */}
      <section className="relative py-16 sm:py-24 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=2689&auto=format&fit=crop"
            alt="City"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#2a3890]/90 mix-blend-multiply"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">Ready to Build Your Vision?</h2>
            <p className="mb-8 sm:mb-10 text-blue-100 text-base sm:text-xl max-w-2xl mx-auto">
              Partner with a team that brings over 16+ years of global expertise to your doorstep.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
              <button className="bg-white text-[#2a3890] px-8 py-3 sm:px-10 sm:py-4 font-bold transition-transform hover:scale-105 shadow-xl text-sm sm:text-base"
                onClick={() => window.location.href = '/contact'}
              >
                Get a Quote
              </button>
              <button className="bg-transparent border-2 border-white text-white px-8 py-3 sm:px-10 sm:py-4 font-bold transition-colors hover:bg-white/10 text-sm sm:text-base"
                onClick={() => window.location.href = '/portfolio-summary'}
              >
                View Projects
              </button>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default AboutUs;